import { useEffect, useState } from "react";
import { Spin, Switch, Table, Tooltip } from "antd";
import CommonObj from "../../CommonObj";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { message } from "../../components/antdMessageBridge";
import "../../components/commonpagination/common-pagination.css";
import { refreshFieldPermissionsCache } from "../../hooks/useFieldPermissions";

const FieldAccessControls = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiNoResults, setApiNoResults] = useState(false);
    const [updatingRoleIds, setUpdatingRoleIds] = useState([]);
    const { accessToken } = useAuthToken();

    const handleRestrictionToggle = async (roleId, checked) => {
        const previousData = data;

        setUpdatingRoleIds((prev) => [...prev, roleId]);
        setData((prev) =>
            prev.map((item) =>
                item.roleId === roleId
                    ? { ...item, editRestrictFieldAccess: checked }
                    : item
            )
        );

        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };

            const endpoint =
                CommonObj?.Endpoints?.UpdateRestriction ||
                "/api/Role/UpdateRestriction";

            const payload = {
                roleId,
                isEditRestricted: checked,
            };

            const response = await fetch(endpoint, {
                method: "PUT",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("text/html")) {
                throw new Error("Role restriction API returned HTML instead of JSON.");
            }

            if (!response.ok) {
                throw new Error("Failed to update restriction.");
            }

            const result = await response.json();
            message.success(result?.message || "Restriction updated successfully.");
            await refreshFieldPermissionsCache(accessToken);
        } catch (err) {
            setData(previousData);
            message.error("Unable to update restriction. Please try again.");
            console.log("Update restriction error:", err);
        } finally {
            setUpdatingRoleIds((prev) => prev.filter((id) => id !== roleId));
        }
    };

    const columns = [
                 {
            title: "Role Name",
            dataIndex: "roleName",
            key: "roleName",
            width: 300,
        },
        {
            title: "Assigned Users",
            dataIndex: "assignedUsers",
            key: "assignedUsers",
            width: 300,        },
        {
            title: "Edit Restricted Manual Fields",
            dataIndex: "editRestrictFieldAccess",
            key: "editRestrictFieldAccess",
            width: 300,
            render: (value, record) => (
                <Tooltip
                    title="Enable to allow manual overrides on restricted fields"
                    placement="right"
                    color="#ffffff"
                    overlayInnerStyle={{
                        color: "#1f2937",
                        border: "1px solid #d9d9d9",
                    }}
                >
                    <Switch
                        checked={Boolean(value)}
                        loading={updatingRoleIds.includes(record.roleId)}
                        disabled={updatingRoleIds.includes(record.roleId)}
                        data-track={
                            Boolean(value)
                                ? "field-role-management-disable-restriction"
                                : "field-role-management-enable-restriction"
                        }
                        onChange={(checked) =>
                            handleRestrictionToggle(record.roleId, checked)
                        }
                    />
                </Tooltip>
            ),
        },
    ];

        const fetchData = async () => {
        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };

            const endpoint =
                CommonObj?.Endpoints?.GetRoleManagementList ||
                "/api/Role/GetRoleManagementList";

            const response = await fetch(endpoint, {
                method: "GET",
                headers: myHeaders,
            });

            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("text/html")) {
                throw new Error("Role management list API returned HTML instead of JSON.");
            }

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const result = await response.json();
            let formattedData = [];

            if (Array.isArray(result)) {
                formattedData = result;
            } else if (Array.isArray(result?.data)) {
                formattedData = result.data;
            } else if (Array.isArray(result?.items)) {
                formattedData = result.items;
            }

            setData(formattedData);
            setApiNoResults(formattedData.length === 0);
        } catch (err) {
            console.log("Fetch role management list error:", err);
            message.error(
                "Unable to load role management list. Please verify API base URL and endpoint routing."
            );
            setData([]);
            setApiNoResults(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return <Spin size="large" style={{ margin: "20px auto" }} />;
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Field Access Control</h4>
                </div>
            </div>

            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        locale={{
                            emptyText: apiNoResults
                                ? "No records found."
                                : "No Data available.",
                        }}
                        pagination={false}
                        rowKey={(record) => record.roleId}
                        scroll={{
                            x: "max-content",
                            y: "calc(85vh - 280px)",
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default FieldAccessControls;
